import { TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';

import { INTEGRATION_PLATFORMS } from '../api/shared';
import { useT } from '../i18n/LanguageContext';

import { LIMIT_FIELDS, type LicenceDraft, type LimitField } from './licenceDraft';

const LIMIT_LABELS: Record<
  LimitField,
  | 'licence.metric.companies'
  | 'licence.metric.subUsers'
  | 'licence.metric.products'
  | 'licence.metric.totalStock'
> = {
  maxCompanies: 'licence.metric.companies',
  maxSubUsers: 'licence.metric.subUsers',
  maxProducts: 'licence.metric.products',
  maxTotalStock: 'licence.metric.totalStock',
};

type LicenceFieldsProps = {
  draft: LicenceDraft;
  onChange: (next: LicenceDraft) => void;
  showNotes?: boolean;
  /** Off for a licence order, whose period follows from its payment (R0006 D-05). */
  showDates?: boolean;
};

/**
 * Validity, the four limits and the linked shops. An empty limit is unlimited and the helper text says so;
 * an empty number of shops is none.
 */
export const LicenceFields = ({
  draft,
  onChange,
  showNotes = false,
  showDates = true,
}: LicenceFieldsProps) => {
  const t = useT();

  return (
    <Grid container spacing={2}>
      {showDates ? (
        <>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              required
              type="date"
              label={t('licence.validFrom')}
              value={draft.validFrom}
              onChange={(event) => onChange({ ...draft, validFrom: event.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              type="date"
              label={t('licence.validUntil')}
              helperText={t('licence.validUntilHint')}
              value={draft.validUntil}
              onChange={(event) => onChange({ ...draft, validUntil: event.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </>
      ) : null}
      {LIMIT_FIELDS.map((field) => (
        <Grid key={field} size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label={t(LIMIT_LABELS[field])}
            placeholder={t('common.unlimited')}
            helperText={t('licence.limitHint')}
            value={draft[field]}
            inputProps={{ inputMode: 'numeric' }}
            onChange={(event) => onChange({ ...draft, [field]: event.target.value })}
          />
        </Grid>
      ))}
      {INTEGRATION_PLATFORMS.map((platform) => (
        <Grid key={platform} size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label={t('licence.metric.integrationLinks', { platform: t(`platform.${platform}`) })}
            placeholder="0"
            helperText={t('licence.integrationHint')}
            value={draft.integrationLinks[platform]}
            inputProps={{ inputMode: 'numeric' }}
            onChange={(event) =>
              onChange({
                ...draft,
                integrationLinks: { ...draft.integrationLinks, [platform]: event.target.value },
              })
            }
          />
        </Grid>
      ))}
      {showNotes ? (
        <Grid size={12}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={t('licence.notes')}
            value={draft.notes}
            onChange={(event) => onChange({ ...draft, notes: event.target.value })}
          />
        </Grid>
      ) : null}
    </Grid>
  );
};
